import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import client from '@/api/client';
import type { User, PageResponse, UserRole } from '@/api/types';
import { UserCheck, UserX, Key, Search, Trash2 } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';

const ROLE_COLORS: Record<UserRole, string> = {
  ADMIN: 'bg-blue-100 text-blue-800',
  HOSTEL_OWNER: 'bg-teal-100 text-teal-800',
  SUPERVISOR: 'bg-violet-100 text-violet-800',
  TENANT: 'bg-amber-100 text-amber-800',
};

const LINKED_TO_OWNER: Partial<Record<UserRole, boolean>> = {
  SUPERVISOR: true,
};

export function AdminUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newPwd, setNewPwd] = useState<{ userId: string; pwd: string } | null>(null);
  const { page, size, setPage, resetPage } = usePagination();

  function runSearch() {
    setSearchQuery(searchInput.trim());
    resetPage();
  }

  const { data, isLoading } = useQuery<PageResponse<User>>({
    queryKey: ['admin-users', roleFilter, searchQuery, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (roleFilter) params.set('role', roleFilter);
      if (searchQuery) params.set('search', searchQuery);
      return client.get(`/api/admin/users?${params}`).then(r => r as unknown as PageResponse<User>);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => client.patch(`/api/admin/users/${id}/toggle-active`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const resetPwdMutation = useMutation({
    mutationFn: ({ id, pwd }: { id: string; pwd: string }) =>
      client.post(`/api/admin/users/${id}/reset-password`, { newPassword: pwd }),
    onSuccess: () => setNewPwd(null),
  });

  const approveDeletionMutation = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/users/${id}/approve-deletion`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const users = data?.content ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('nav.users')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
              placeholder={t('supervisor.searchNameEmail', 'Search name / email / phone…')}
              className="border border-gray-300 rounded-l-lg px-3 py-1.5 text-sm w-56"
            />
            <button
              onClick={runSearch}
              aria-label={t('common.search', 'Search')}
              className="border border-l-0 border-gray-300 rounded-r-lg px-2.5 py-1.5 text-gray-500 hover:bg-gray-50"
            >
              <Search size={15} />
            </button>
          </div>
          <Select
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value as UserRole | ''); resetPage(); }}
            className="w-auto"
            options={[
              { value: '', label: t('admin.allRoles', 'All Roles') },
              { value: 'HOSTEL_OWNER', label: t('admin.hostelOwners', 'Hostel Owners') },
              { value: 'SUPERVISOR', label: t('nav.supervisors') },
              { value: 'TENANT', label: t('nav.tenants', 'Tenants') },
            ]}
          />
        </div>
      </div>

      {isLoading ? <p className="text-gray-400">{t('common.loading')}</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">{t('common.name')}</th>
                <th className="px-4 py-3 text-left">{t('common.email')}</th>
                <th className="px-4 py-3 text-left">{t('admin.role', 'Role')}</th>
                <th className="px-4 py-3 text-left">{t('admin.onboardedBy', 'Onboarded By')}</th>
                <th className="px-4 py-3 text-left">{t('common.status')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => {
                const openProfile = u.tenantId ? () => void navigate({ to: '/admin/tenants/$tenantId', params: { tenantId: u.tenantId! }, search: { tab: 'profile' } }) : undefined;
                return (
                <tr
                  key={u.id}
                  onClick={openProfile}
                  title={openProfile ? t('owner.viewProfile', 'View Profile') : undefined}
                  className={`hover:bg-gray-50 ${openProfile ? 'cursor-pointer' : ''}`}
                >
                  <td className="px-4 py-3 font-medium">{u.fullName}</td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role]}`}>
                      {u.role === 'HOSTEL_OWNER' ? t('admin.ownerLabel', 'Owner') : u.role === 'SUPERVISOR' ? t('admin.supervisorLabel', 'Supervisor') : u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {LINKED_TO_OWNER[u.role] ? (
                      <span className="text-sm text-gray-600">
                        {u.createdByName ? (
                          <span className="flex items-center gap-1">
                            <span className="text-teal-600 font-medium">{u.createdByName}</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">{t('admin.noOwner', 'No owner')}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                      {u.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleMutation.mutate(u.id)}
                        title={u.isActive ? t('admin.deactivate', 'Deactivate') : t('admin.activate', 'Activate')}
                        className="p-1.5 rounded hover:bg-gray-100"
                      >
                        {u.isActive ? <UserX size={15} className="text-red-500" /> : <UserCheck size={15} className="text-green-600" />}
                      </button>
                      {u.role !== 'ADMIN' && (
                        <button
                          onClick={() => setNewPwd({ userId: u.id, pwd: '' })}
                          title={t('admin.resetPassword', 'Reset password')}
                          className="p-1.5 rounded hover:bg-gray-100"
                        >
                          <Key size={15} className="text-blue-500" />
                        </button>
                      )}
                      {u.deletionRequested && (
                        <button
                          onClick={() => approveDeletionMutation.mutate(u.id)}
                          title={t('admin.approveDeletion', 'Approve account deletion')}
                          className="p-1.5 rounded hover:bg-red-50"
                        >
                          <Trash2 size={15} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {users.length === 0 && (
            <p className="text-center text-gray-400 py-10">{t('common.noData')}</p>
          )}
          <Pagination
            page={page}
            totalPages={data?.totalPages ?? 0}
            totalElements={data?.totalElements ?? 0}
            size={size}
            onPageChange={setPage}
            className="px-4 pb-3"
          />
        </div>
      )}

      {newPwd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-80 shadow-xl">
            <h3 className="font-semibold mb-4">{t('admin.resetPasswordTitle', 'Reset Password')}</h3>
            <input
              type="password"
              placeholder={t('admin.newPasswordPlaceholder', 'New password (8+ chars)')}
              value={newPwd.pwd}
              onChange={e => setNewPwd(p => p ? { ...p, pwd: e.target.value } : null)}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => resetPwdMutation.mutate({ id: newPwd.userId, pwd: newPwd.pwd })}
                disabled={newPwd.pwd.length < 8}
                className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >{t('common.save')}</button>
              <button onClick={() => setNewPwd(null)} className="flex-1 border rounded-lg py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
